import { GoogleLoginProvider, SocialAuthService, SOCIAL_AUTH_CONFIG } from '@abacritt/angularx-social-login';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  PLATFORM_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import localeEsCo from '@angular/common/locales/es-CO';
import { routes } from './app.routes';
import {
  NoPreloading,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideMilincoDateAdapter } from '@core/dates/milinco-date.providers';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { milincoTranslateLoaderFactory } from '@core/i18n/milinco-translate.loader';

registerLocaleData(localeEsCo);

// icons
import { TablerIconsModule } from 'angular-tabler-icons';
import { MILINCO_CORE_TABLER_ICONS } from '@core/icons/milinco-tabler-icons';

// perfect scrollbar
import { NgScrollbarModule } from 'ngx-scrollbar';
//Import all material modules
import { MaterialModule } from './material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { API_BASE_URL, GOOGLE_MAPS_API_KEY } from '@core/config/api.config';
import { authTokenInterceptor } from '@core/http/interceptors/auth-token.interceptor';
import { tenantCompanyInterceptor } from '@core/http/interceptors/tenant-company.interceptor';
import { tenantLocationInterceptor } from '@core/http/interceptors/tenant-location.interceptor';
import { operatedTenantWriteInterceptor } from '@core/http/interceptors/operated-tenant-write.interceptor';
import { apiErrorInterceptor } from '@core/http/interceptors/api-error.interceptor';
import { ssrApiHostInterceptor } from '@core/http/interceptors/ssr-api-host.interceptor';
import { initializeMilincoTranslate } from '@core/i18n/initialize-translate';
import { AUTH_PROVIDERS } from '@features/auth/auth.providers';
import { CATALOG_PROVIDERS } from '@features/catalog/catalog.providers';
import { ONBOARD_PROVIDERS } from '@features/onboard/onboard.providers';
import { SERVICES_PROVIDERS } from '@features/services/services.providers';
import { MilincoBottomSheetDragDismissService } from '@shared/ui/milinco-sheet-drag-dismiss/milinco-bottom-sheet-drag-dismiss.service';
import { environment } from '../environments/environment';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';

function initializeMilincoBottomSheetDragDismiss(): void {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return;
  }
  inject(MilincoBottomSheetDragDismissService).start();
}

export const appConfig: ApplicationConfig = {
  providers: [
    ...(environment.production && environment.sentryDsn
      ? [{ provide: ErrorHandler, useValue: Sentry.createErrorHandler() }]
      : []),
    provideAnimationsAsync(), // required animations providers
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding(),
      // PreloadAllModules rompe la estabilidad del prerender SSR (timeout al cargar todo el admin).
      withPreloading(NoPreloading),
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        ssrApiHostInterceptor,
        authTokenInterceptor,
        tenantCompanyInterceptor,
        tenantLocationInterceptor,
        operatedTenantWriteInterceptor,
        apiErrorInterceptor,
      ])
    ),
    {
      provide: API_BASE_URL,
      useValue: environment.apiBaseUrl,
    },
    {
      provide: GOOGLE_MAPS_API_KEY,
      useValue: environment.googleMapsApiKey,
    },
    {
      // El fondo del segmento seleccionado ya comunica el estado; el check
      // agrega ancho variable y desborda los toggles en móviles estrechos.
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideSingleSelectionIndicator: true },
    },
    // ngx-translate no localiza MatDatepicker: el adapter usa LOCALE_ID / MAT_DATE_LOCALE.
    { provide: LOCALE_ID, useValue: 'es-CO' },
    { provide: MAT_DATE_LOCALE, useValue: 'es-CO' },
    ...provideMilincoDateAdapter(),
    ...AUTH_PROVIDERS,
    ...CATALOG_PROVIDERS,
    ...ONBOARD_PROVIDERS,
    // PRODUCT_CATALOG_PROVIDERS: en product-catalog.routes (lazy) — evita NG0201 SSR.
    ...SERVICES_PROVIDERS,
    SocialAuthService,
    {
      provide: SOCIAL_AUTH_CONFIG,
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(environment.googleClientId),
          },
        ],
      },
    },
    importProvidersFrom(
      FormsModule,
      ReactiveFormsModule,
      MaterialModule,
      TablerIconsModule.pick(MILINCO_CORE_TABLER_ICONS),
      NgScrollbarModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: milincoTranslateLoaderFactory,
        },
      })
    ),
    provideAppInitializer(initializeMilincoTranslate),
    provideAppInitializer(initializeMilincoBottomSheetDragDismiss),
    provideClientHydration(withEventReplay()),
  ],
};

